import { Component, Input, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  IBillingAccount,
  IBillingDocument,
  IBillingTransactions,
  IPaymentInfo,
  PaymentStatus,
} from '@dch/billing-business-logic';
import { TranslationService, LanguageService } from '@dch/ng-common';
import { filter, tap } from 'rxjs/operators';
import { getPromiseOfPaymentRequest } from 'src/app/state/actions';
import { ApplicationState } from 'src/app/state/reducers';
import { selectCollectionCardMessage } from 'src/app/state/selectors/billing-balance.selector';
import { selectCustomerAccount } from 'src/app/state/selectors/customer-account.selector';
import { OnDestroyHookDirective } from '../../common/hooks';
import { MessageBoxTypesEnum } from '@dch/pxs-angular-design-system/src/lib/messages/message-box';
import { selectPaymentInfoByAccountNumber } from 'src/app/state/selectors/billing-payment-info.selectors';
import { billingAccountsService } from '@dch/billing-business-logic';

@Component({
  selector: 'my-bill-promise-of-payment-container',
  templateUrl: './my-bill-promise-of-payment-container.component.html',
  standalone: false,
})
export class MyBillPromiseOfPaymentContainerComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  @Input() billingAccount: IBillingAccount;
  @Input() hasInstalmentPlan: boolean;
  @Input() billingTransactions: IBillingTransactions;
  paymentInfo?: IPaymentInfo;
  messageBoxType: MessageBoxTypesEnum;
  messageBoxTypeError = MessageBoxTypesEnum.Information;
  messageBoxTypeInfo = MessageBoxTypesEnum.Information;
  messageBoxTitle: string;
  messageBoxContent: string;
  hasPaymentRefused = false;
  billingDocument: IBillingDocument;
  segmentUserKey = '';
  customerId: number;

  readonly selectedCustomerAccount$ = this.store.select(selectCustomerAccount);
  constructor(
    private store: Store<ApplicationState>,
    private translationService: TranslationService,
    private languageService: LanguageService
  ) {
    super();
  }

  ngOnInit() {
    this.store.dispatch(
      getPromiseOfPaymentRequest({
        billingAccountNumber: this.billingAccount.accountNumber,
      })
    );
    this.setPaymentInfo();
    this.setSelectedCustomerAccountSegmentUserKey();
    this.addSubscription(
      this.store
        .select(selectCollectionCardMessage(this.billingAccount.accountNumber))
        .pipe(
          tap(
            ({ collectionMessage, softRefused, hardRefused, bankAccount }) => {
              if (softRefused || hardRefused) {
                this.hasPaymentRefused = true;
              }
              if (
                (collectionMessage || softRefused || hardRefused) &&
                this.hasInstalmentPlan
              ) {
                const openBillingDocuments =
                  this.billingTransactions.openTransactions.billingDocuments.filter(
                    (billingDocument) =>
                      billingDocument.paymentStatus !==
                        PaymentStatus.ONGOING_PAYMENT &&
                      billingDocument.paymentStatus !== PaymentStatus.INSTALMENT
                  );

                this.addIPMessage(openBillingDocuments);
              } else if (collectionMessage) {
                this.messageBoxType = MessageBoxTypesEnum.Error;
                this.messageBoxTitle =
                  this.translationService.getTranslatedValueFor(
                    'bills.collectionCard.title'
                  );
                this.messageBoxContent =
                  this.setBillOrPaymentStatementTranslationByLanguage(
                    collectionMessage
                  );
              } else if (softRefused) {
                this.messageBoxType = MessageBoxTypesEnum.Error;
                this.messageBoxTitle =
                  this.translationService.getTranslatedValueFor(
                    'bills.collectionCard.titleRefusedDirectDebit'
                  );
                this.messageBoxContent = this.translationService
                  .getTranslatedValueFor(
                    'bills.collectionCard.softRefusedDirectDebitMessage'
                  )
                  .replace('{{bankAccount}}', bankAccount);
              } else if (hardRefused) {
                this.messageBoxType = MessageBoxTypesEnum.Error;
                this.messageBoxTitle =
                  this.translationService.getTranslatedValueFor(
                    'bills.collectionCard.titleRefusedDirectDebit'
                  );
                this.messageBoxContent = this.translationService
                  .getTranslatedValueFor(
                    'bills.collectionCard.hardRefusedDirectDebitMessage'
                  )
                  .replace('{{bankAccount}}', bankAccount);
              }
            }
          )
        )
        .subscribe()
    );
  }

  private setSelectedCustomerAccountSegmentUserKey() {
    this.addSubscription(
      this.selectedCustomerAccount$
        .pipe(
          filter(
            (selectedCustomerAccount) => selectedCustomerAccount !== undefined
          ),
          tap((selectedCustomerAccount) => {
            this.customerId =
              selectedCustomerAccount.getSelectedCustomer().customerId;
            this.segmentUserKey =
              selectedCustomerAccount.getSelectedCustomer().subsegment === 'RES'
                ? 'RES.'
                : '';
          })
        )
        .subscribe()
    );
  }

  private addMessage(titleKey: string, contentKey: string) {
    this.messageBoxTitle =
      this.translationService.getTranslatedValueFor(titleKey);
    this.messageBoxContent =
      this.translationService.getTranslatedValueFor(contentKey);
  }

  private addIPMessage(nonOngoingBillingDocuments: IBillingDocument[]) {
    this.messageBoxType = MessageBoxTypesEnum.Information;
    if (
      billingAccountsService.isDirectDebitPaymentMethod(
        this.paymentInfo?.paymentMethod
      )
    ) {
      if (nonOngoingBillingDocuments.length > 1) {
        this.addMessage(
          'bills.collectionCard.IPDDTitle',
          `bills.collectionCard.${this.segmentUserKey}IPDDMessage`
        );
      } else if (nonOngoingBillingDocuments.length === 1) {
        this.billingDocument = nonOngoingBillingDocuments[0];
        this.addMessage(
          'bills.collectionCard.IPDDOneOpenInvoiceTitle',
          `bills.collectionCard.${this.segmentUserKey}IPDDOneOpenInvoiceMessage`
        );
      }
    } else {
      if (nonOngoingBillingDocuments.length > 1) {
        this.addMessage(
          'bills.collectionCard.IPManualTitle',
          `bills.collectionCard.${this.segmentUserKey}IPManualMessage`
        );
      } else if (nonOngoingBillingDocuments.length === 1) {
        this.billingDocument = nonOngoingBillingDocuments[0];
        this.addMessage(
          'bills.collectionCard.IPManualOneOpenInvoiceTitle',
          `bills.collectionCard.${this.segmentUserKey}IPManualOneOpenInvoiceMessage`
        );
      }
    }
  }

  private setPaymentInfo() {
    const paymentInfo$ = this.store.select(
      selectPaymentInfoByAccountNumber(this.billingAccount.accountNumber)
    );
    this.addSubscription(
      paymentInfo$
        .pipe(
          filter((paymentInfo) => {
            return paymentInfo && paymentInfo.result !== null;
          }),
          tap((paymentInfo) => {
            this.paymentInfo = paymentInfo.result;
          })
        )
        .subscribe()
    );
  }

  private setBillOrPaymentStatementTranslationByLanguage(
    collectionMessage: string
  ): string {
    const language = this.languageService.getSelectedLanguage();
    const isResidential = this.segmentUserKey === 'RES.';

    switch (language) {
      case 'fr':
        return isResidential
          ? this.replaceFrenchTerms(collectionMessage)
          : collectionMessage;
      case 'nl':
        return isResidential
          ? this.replaceDutchTerms(collectionMessage)
          : collectionMessage;
      case 'de':
        return isResidential
          ? this.replaceGermanTerms(collectionMessage)
          : collectionMessage;
      default:
        return this.replaceEnglishTerms(collectionMessage, isResidential);
    }
  }

  private replaceFrenchTerms(message: string): string {
    return message.replace(/facture|factures/g, (match) =>
      match === 'facture' ? 'décompte' : 'décomptes'
    );
  }

  private replaceDutchTerms(message: string): string {
    return message.replace(/factuur|facturen/g, (match) =>
      match === 'factuur' ? 'aanrekening' : 'aanrekeningen'
    );
  }

  private replaceGermanTerms(message: string): string {
    return message.replace(/Rechnung|Rechnungen/g, (match) =>
      match === 'Rechnung' ? 'Zahlungsaufstellung' : 'Zahlungsaufstellungen'
    );
  }

  private replaceEnglishTerms(message: string, isResidential: boolean): string {
    return message.replace(/invoice|invoices/g, (match) => {
      if (isResidential) {
        return match === 'invoice' ? 'payment statement' : 'payment statements';
      }
      return match === 'invoice' ? 'bill' : 'bills';
    });
  }
}
